import type { PersonType, RentalType } from "@/shared/types/enums";
import type {
  RentalCreateClient,
  RentalCreateContract,
  RentalCreateRequestDTO,
} from "../types/rental.types";
import { datetimeLocalToIso } from "@/shared/lib/date-format";

export interface NewClientData {
  name: string;
  document: string;
  personType: PersonType;
  /** Omitido para PJ. */
  gender?: "M" | "F" | "";
  phone: string;
  email?: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  state: string;
  postalCode: string;
}

export interface NewContractData {
  contractNumber: string;
  contractImage?: string;
}

export interface RentalCreateFormState {
  rentalType: RentalType;
  sellerId: number;
  hasShoes: boolean;
  shoesPairQuantity: number;
  totalValue: number;
  payments: RentalCreateRequestDTO["payments"];
  expectedOutAt: string;
  expectedInAt: string;
  description: string;
  emitInvoice: boolean;
  invoiceObservation: string;
  selectedClientId: number | null;
  newClient: NewClientData | null;
  selectedContractId: number | null;
  newContract: NewContractData | null;
  parentContractId: number | null;
  stockIds: number[];
  gownItems: { productId: number; quantity: number }[];
  tagIds: number[];
}

function buildContract(state: RentalCreateFormState): RentalCreateContract {
  const related = state.parentContractId
    ? { relatedContractsIds: [state.parentContractId] }
    : {};

  if (state.selectedContractId) {
    return { contractId: state.selectedContractId, ...related };
  }

  if (!state.newContract) {
    throw new Error("Cliente ou contrato não definidos no formulário");
  }

  return {
    contractNumber: state.newContract.contractNumber,
    ...(state.newContract.contractImage
      ? { contractImage: state.newContract.contractImage }
      : {}),
    ...related,
  };
}

function buildClient(state: RentalCreateFormState): RentalCreateClient {
  if (state.selectedClientId) {
    return { clientId: state.selectedClientId };
  }

  if (!state.newClient) {
    throw new Error("Cliente não definido no formulário");
  }

  const c = state.newClient;
  return {
    name: c.name,
    document: c.document,
    ...(c.personType ? { personType: c.personType } : {}),
    ...(c.gender ? { gender: c.gender } : {}),
    phone: c.phone,
    ...(c.email ? { email: c.email } : {}),
    street: c.street,
    number: c.number,
    complement: c.complement,
    neighborhood: c.neighborhood,
    state: c.state,
    postalCode: c.postalCode,
  };
}

export function buildCreatePayload(
  state: RentalCreateFormState,
): RentalCreateRequestDTO {
  return {
    sellerId: state.sellerId,
    rentalType: state.rentalType,
    hasShoes: state.rentalType === "SUIT" ? state.hasShoes : false,
    shoesPairQuantity:
      state.rentalType === "SUIT" && state.hasShoes
        ? state.shoesPairQuantity
        : null,
    totalValue: state.totalValue,
    payments: state.payments,
    expectedOutAt: datetimeLocalToIso(state.expectedOutAt),
    expectedInAt: datetimeLocalToIso(state.expectedInAt),
    description: state.description?.trim() ? state.description : null,
    emitInvoice: state.emitInvoice,
    ...(state.invoiceObservation?.trim()
      ? { invoiceObservation: state.invoiceObservation.trim() }
      : {}),
    contract: buildContract(state),
    client: buildClient(state),
    ...(state.rentalType === "SUIT" ? { stockIds: state.stockIds } : {}),
    ...(state.rentalType === "GOWN" ? { gownItems: state.gownItems } : {}),
    ...(state.tagIds.length > 0 ? { tagIds: state.tagIds } : {}),
  };
}
